/**
 * Product Card Hover Actions
 * Handles Quick Add, Quick View, and Wishlist functionality
 */

document.addEventListener('DOMContentLoaded', function() {
  const ProductCardActions = {
    init() {
      this.initQuickAdd();
      this.initWishlist();
      this.initQuickView();
      this.checkWishlistStates();
    },

    // ═══════════════════════════════════════════════════════════════════════
    // QUICK ADD TO CART
    // ═══════════════════════════════════════════════════════════════════════
    initQuickAdd() {
      document.querySelectorAll('[data-quick-add-btn]').forEach(btn => {
        btn.addEventListener('click', (e) => this.handleQuickAdd(e, btn));
      });
    },

    async handleQuickAdd(e, btn) {
      e.preventDefault();
      e.stopPropagation();

      const hasVariants = btn.dataset.hasVariants === 'true';
      const productUrl = btn.dataset.productUrl;
      const variantId = btn.dataset.variantId;
      const available = btn.dataset.available === 'true';

      // If product has multiple variants, go to product page
      if (hasVariants) {
        window.location.href = productUrl;
        return;
      }

      // If not available, don't add
      if (!available) {
        this.showNotification('This product is currently unavailable', 'error');
        return;
      }

      // Show loading state
      btn.classList.add('is-loading');
      btn.disabled = true;

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            items: [{
              id: parseInt(variantId),
              quantity: 1
            }]
          })
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.description || 'Failed to add to cart');
        }

        // Show success state
        btn.classList.remove('is-loading');
        btn.classList.add('is-added');

        // Update cart count
        await this.updateCartCount();

        // Dispatch event for cart drawer
        document.dispatchEvent(new CustomEvent('cart:updated'));

        // Show notification
        this.showNotification('Product added to cart!', 'success');

        // Reset button after delay
        setTimeout(() => {
          btn.classList.remove('is-added');
          btn.disabled = false;
        }, 2000);

      } catch (error) {
        console.error('Quick add error:', error);
        btn.classList.remove('is-loading');
        btn.disabled = false;
        this.showNotification(error.message || 'Error adding to cart', 'error');
      }
    },

    async updateCartCount() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        
        document.querySelectorAll('[data-cart-count]').forEach(el => {
          el.textContent = cart.item_count;
          if (cart.item_count > 0) {
            el.style.display = '';
            // Animate
            el.style.transform = 'scale(1.3)';
            setTimeout(() => { el.style.transform = 'scale(1)'; }, 200);
          }
        });
      } catch (error) {
        console.error('Error updating cart count:', error);
      }
    },

    // ═══════════════════════════════════════════════════════════════════════
    // WISHLIST
    // ═══════════════════════════════════════════════════════════════════════
    initWishlist() {
      document.querySelectorAll('[data-wishlist-btn]').forEach(btn => {
        btn.addEventListener('click', (e) => this.handleWishlist(e, btn));
      });
    },

    handleWishlist(e, btn) {
      e.preventDefault();
      e.stopPropagation();

      const productData = {
        id: btn.dataset.productId,
        title: btn.dataset.productTitle,
        image: btn.dataset.productImage,
        price: btn.dataset.productPrice,
        url: btn.dataset.productUrl
      };

      const wishlist = this.getWishlist();
      const existingIndex = wishlist.findIndex(item => item.id === productData.id);

      if (existingIndex > -1) {
        // Remove from wishlist
        wishlist.splice(existingIndex, 1);
        btn.classList.remove('is-active');
        this.showNotification('Removed from wishlist', 'info');
      } else {
        // Add to wishlist
        wishlist.push(productData);
        btn.classList.add('is-active');
        this.showNotification('Added to wishlist!', 'success');
      }

      this.saveWishlist(wishlist);
      this.updateWishlistCount();
    },

    getWishlist() {
      return JSON.parse(localStorage.getItem('luxe_wishlist') || '[]');
    },

    saveWishlist(wishlist) {
      localStorage.setItem('luxe_wishlist', JSON.stringify(wishlist));
    },

    checkWishlistStates() {
      const wishlist = this.getWishlist();
      const wishlistIds = wishlist.map(item => item.id);

      document.querySelectorAll('[data-wishlist-btn]').forEach(btn => {
        const productId = btn.dataset.productId;
        if (wishlistIds.includes(productId)) {
          btn.classList.add('is-active');
        }
      });

      this.updateWishlistCount();
    },

    updateWishlistCount() {
      const wishlist = this.getWishlist();
      document.querySelectorAll('[data-wishlist-count]').forEach(el => {
        el.textContent = wishlist.length;
        if (wishlist.length > 0) {
          el.style.display = '';
        } else {
          el.style.display = 'none';
        }
      });
    },

    // ═══════════════════════════════════════════════════════════════════════
    // QUICK VIEW
    // ═══════════════════════════════════════════════════════════════════════
    initQuickView() {
      document.querySelectorAll('[data-quick-view-btn]').forEach(btn => {
        btn.addEventListener('click', (e) => this.handleQuickView(e, btn));
      });
    },

    async handleQuickView(e, btn) {
      e.preventDefault();
      e.stopPropagation();

      const productUrl = btn.dataset.productUrl;
      const productHandle = btn.dataset.productHandle;

      // Check if theme has a quick view modal system
      const quickViewModal = document.querySelector('#quick-view-modal, [data-quick-view-modal]');
      
      if (quickViewModal) {
        // Use existing quick view modal if available
        try {
          const response = await fetch(`${productUrl}?view=quick-view`);
          const html = await response.text();
          
          const modalContent = quickViewModal.querySelector('[data-modal-content]') || quickViewModal;
          modalContent.innerHTML = html;
          
          // Show modal
          if (typeof quickViewModal.showModal === 'function') {
            quickViewModal.showModal();
          } else {
            quickViewModal.classList.add('is-open');
            document.body.classList.add('modal-open');
          }
        } catch (error) {
          console.error('Quick view error:', error);
          // Fallback: go to product page
          window.location.href = productUrl;
        }
      } else {
        // Fallback: Create simple quick view modal
        this.createQuickViewModal(productUrl);
      }
    },

    async createQuickViewModal(productUrl) {
      // Show loading
      this.showNotification('Loading product...', 'info');

      try {
        // Fetch product JSON
        const response = await fetch(`${productUrl}.js`);
        const product = await response.json();

        // Create modal HTML
        const modalHtml = `
          <div class="quick-view-overlay" data-quick-view-overlay>
            <div class="quick-view-modal" data-quick-view-modal-content>
              <button type="button" class="quick-view-close" data-quick-view-close aria-label="Close">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
              <div class="quick-view-content">
                <div class="quick-view-image">
                  <img src="${product.featured_image}" alt="${product.title}">
                </div>
                <div class="quick-view-info">
                  <h2 class="quick-view-title">${product.title}</h2>
                  <p class="quick-view-price">${this.formatMoney(product.price)}</p>
                  <div class="quick-view-description">${product.description ? product.description.substring(0, 200) + '...' : ''}</div>
                  <a href="${productUrl}" class="quick-view-btn-full">View Full Details</a>
                </div>
              </div>
            </div>
          </div>
        `;

        // Add modal to body
        document.body.insertAdjacentHTML('beforeend', modalHtml);

        // Add close handlers
        const overlay = document.querySelector('[data-quick-view-overlay]');
        const closeBtn = document.querySelector('[data-quick-view-close]');

        overlay.addEventListener('click', (e) => {
          if (e.target === overlay) {
            overlay.remove();
          }
        });

        closeBtn.addEventListener('click', () => overlay.remove());

        document.addEventListener('keydown', function closeOnEscape(e) {
          if (e.key === 'Escape') {
            overlay.remove();
            document.removeEventListener('keydown', closeOnEscape);
          }
        });

      } catch (error) {
        console.error('Quick view error:', error);
        // Fallback: go to product page
        window.location.href = productUrl;
      }
    },

    formatMoney(cents) {
      return new Intl.NumberFormat('el-GR', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'EUR'
      }).format(cents / 100);
    },

    // ═══════════════════════════════════════════════════════════════════════
    // NOTIFICATIONS
    // ═══════════════════════════════════════════════════════════════════════
    showNotification(message, type = 'info') {
      // Check if notification container exists
      let container = document.querySelector('.pca-notification-container');
      
      if (!container) {
        container = document.createElement('div');
        container.className = 'pca-notification-container';
        document.body.appendChild(container);
      }

      const notification = document.createElement('div');
      notification.className = `pca-notification pca-notification--${type}`;
      
      const icons = {
        success: '✓',
        error: '✕',
        info: 'ℹ'
      };
      
      notification.innerHTML = `
        <span class="pca-notification__icon">${icons[type] || icons.info}</span>
        <span class="pca-notification__text">${message}</span>
      `;

      container.appendChild(notification);

      // Animate in
      requestAnimationFrame(() => {
        notification.classList.add('is-visible');
      });

      // Remove after delay
      setTimeout(() => {
        notification.classList.remove('is-visible');
        setTimeout(() => notification.remove(), 300);
      }, 3000);
    }
  };

  // Initialize
  ProductCardActions.init();

  // Re-initialize when new content is loaded (infinite scroll, etc.)
  document.addEventListener('shopify:section:load', () => ProductCardActions.init());
  
  // Also watch for dynamically added product cards
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1 && node.querySelector('[data-product-card-actions]')) {
          ProductCardActions.init();
        }
      });
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });
});
